import type { InvocationContext, RuntimeContext } from 'hypertube-nodejs-sdk'

import { UnifiedGraftModelTypeKey } from '@/core/const/unifiedGraftModelTypeKeys.const'
import type { ExecutableMember } from '@/core/types/library.type'
import { isGlobalMethodType } from '@/core/utils/isGlobalMethodType'

type RunMemberInvocationParams = {
  runtimeContext: RuntimeContext
  typeContext?: InvocationContext
  member: ExecutableMember
  methodParams?: unknown[]
  instanceParams?: unknown[]
  constructorKey?: string
}

export const runMemberInvocation = ({
  runtimeContext,
  typeContext,
  member,
  methodParams = [],
  instanceParams = [],
}: RunMemberInvocationParams) => {
  switch (member.kind) {
    case 'field': {
      const { field } = member

      if (field.type === UnifiedGraftModelTypeKey.GLOBAL_FIELD) {
        return runtimeContext.getGlobalField(field.name).execute()
      }

      if (!typeContext) {
        throw new Error(`runMemberInvocation: typeContext is required for non-global field "${field.name}"`)
      }

      if (field.type === UnifiedGraftModelTypeKey.STATIC_FIELD) {
        return typeContext.getStaticField(field.name).execute()
      }

      return typeContext
        .createInstance(...instanceParams)
        .getInstanceField(field.name)
        .execute()
    }

    case 'method': {
      const { method, isStatic } = member

      if (isGlobalMethodType(method.type)) {
        return runtimeContext.invokeGlobalFunction(method.name, ...methodParams).execute()
      }

      if (!typeContext) {
        throw new Error(`runMemberInvocation: typeContext is required for non-global method "${method.name}"`)
      }

      if (isStatic) {
        return typeContext.invokeStaticMethod(method.name, ...methodParams).execute()
      }

      return typeContext
        .createInstance(...instanceParams)
        .invokeInstanceMethod(method.name, ...methodParams)
        .execute()
    }

    case 'constructor': {
      if (!typeContext) {
        throw new Error('runMemberInvocation: typeContext is required for constructor invocation')
      }

      return typeContext.createInstance(...instanceParams).execute()
    }
  }
}
